<script lang="ts">
  /* The three things you can do TO the chart: look at it in 3D, ask it for a
   * route, measure across it.
   *
   * ═══ EXTRACTED SO THE TWO LAYOUTS CANNOT DRIFT ═══
   *
   * On a desktop these are a column of compact buttons floating at the right
   * edge. On a phone they are rows in a sheet, because the column was 150 px of
   * a 660 px screen spent on three buttons, two of which most sessions never
   * touch. Same markup, same handlers, two shapes — because the alternative is
   * two copies of a control whose LABEL is its state, and they would not stay
   * identical.
   *
   * The styles travel WITH the markup rather than staying behind in
   * Chart.svelte: Svelte scopes styles to the component that declares them, so
   * leaving `.tool` there would silently unstyle every one of these the moment
   * the markup moved out. That exact fault cost this app a route panel rendered
   * at 16 px — see the note in RouteContent.svelte.
   *
   * ═══ THE LABEL IS THE STATE ═══
   *
   * MEASURE becomes "TAP TWO POINTS" and AUTOROUTE becomes "TAP THE START"
   * while armed, because "tap two points" is not something a chart can imply
   * and an armed tool with no indicator is a mode you cannot see. Nothing may
   * select these by their text for that reason — `.tool.measure`, `.tool.route`
   * and `.tool.d3` are the stable hooks.
   */
  import { measure } from '@gennetay/chart-core/lib/measure.svelte';
  import { threeD, toggle3D } from '@gennetay/chart-core/lib/three-d.svelte';
  import { routePlan } from '@gennetay/chart-core/lib/routeplan.svelte';
  import { measureLabel, routeLabel } from './toollabels.svelte';

  /* ═══ `variant` IS GONE ═══
   *
   * It chose between a column of compact buttons floating at the chart's right
   * edge and a list with a line of explanation each. The floating column does
   * not exist any more — it was 150 px of a 660 px screen for three buttons,
   * two of which most sessions never touch, and it is where MEASURE ended up
   * drawn on top of CACHE. There is one shape now, and it is the readable one.
   */
  let { onpick }: { onpick?: () => void } = $props();

  /* Read through the shared helpers rather than restated here — the dock shows
   * the same strings in its third slot while a tool is armed. */
  const routeTxt = $derived(routeLabel());
  const measureTxt = $derived(measureLabel());
</script>

<div class="tools">
  <button
    class="tool route"
    class:on={routePlan.armed}
    onclick={() => {
      routePlan.armed ? routePlan.off() : routePlan.arm();
      onpick?.();
    }}
    aria-pressed={routePlan.armed}
  >
    <span class="nm">{routeTxt}</span>
    <span class="sub">Solves a route between two taps, and times the narrows to slack.</span>
  </button>

  <button
    class="tool measure"
    class:on={measure.active}
    onclick={() => {
      measure.toggle();
      onpick?.();
    }}
    aria-pressed={measure.active}
  >
    <span class="nm">{measureTxt}</span>
    <span class="sub">Range and bearing between two points on the chart.</span>
  </button>

  <button
    class="tool d3"
    class:on={threeD.on}
    onclick={() => {
      toggle3D();
      onpick?.();
    }}
    aria-pressed={threeD.on}
  >
    <span class="nm">3D</span>
    <span class="sub">Tilts the chart. Costs nothing — the terrain is already there.</span>
  </button>
</div>

<style>
  .tools {
    display: flex;
    flex-direction: column;
    gap: var(--sp-4);
  }
  .tool {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: var(--sp-1);
    text-align: left;
    background: var(--s-2);
    color: var(--ink);
    border: 1px solid var(--line);
    border-radius: var(--r-2);
    padding: var(--sp-4) var(--sp-5);
    min-height: var(--h-touch);
    cursor: pointer;
  }
  .tool:hover {
    border-color: var(--sub);
  }
  /* ═══ ARMED IS A STATE OF THE WHOLE CHART ═══
   *
   * This was `var(--accent, #4a9fd8)` and `--accent` is defined NOWHERE in
   * either theme — so the only feedback that the ruler was live and waiting for
   * a tap was a hard-coded blue appearing in no other part of the design
   * system. `--cy` is the token this app already uses for "on". */
  .tool.on {
    border-color: var(--cy);
    color: var(--cy);
    background: var(--cy-weak);
  }
  .nm {
    font: var(--fs-body) / var(--lh-ui) var(--mono);
    letter-spacing: var(--tr-ui);
  }
  /* There is room here that there was not on the chart edge, so each tool says
     what it does. The single most repeated complaint about the apps this one
     competes with is a feature nobody could find — iNavX's verdict was "so many
     customisations that it makes doing the basics too hard", and Navionics
     hides its charts two levels into a menu. */
  .sub {
    font: var(--fs-read) / var(--lh-read) var(--sans);
    color: var(--sub);
  }
</style>
